/**
 * Vehicle Selector - Firebase Integration
 * Handles Year/Make/Model cascading dropdowns and compatibility checking
 */

(function() {
  'use strict';

  // Firebase Configuration
  const FIREBASE_CONFIG = {
    apiKey: "AIzaSyCng7cVfi_qPkFckaXZ96w40vQ0XoKhQro",
    authDomain: "leitner-designs.firebaseapp.com",
    databaseURL: "https://leitner-designs-fitment.firebaseio.com",
    projectId: "leitner-designs",
    storageBucket: "leitner-designs.firebasestorage.app",
    messagingSenderId: "527135358298",
    appId: "1:527135358298:web:911d1696da219e37d48763"
  };

  // Local storage keys
  const STORAGE_KEY = 'leitner_saved_vehicles';
  const EMAIL_KEY = 'leitner_customer_email';

  // Cache for Firebase data
  let dataCache = {
    years: null,
    makes: null,
    vehicles: null,
    racks: null
  };

  // Firebase database reference
  let db = null;

  /**
   * Initialize Firebase (lazy load SDK)
   */
  async function initFirebase() {
    if (db) return db;

    // Check if Firebase is already loaded
    if (typeof firebase === 'undefined') {
      // Load Firebase SDK dynamically
      await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-app-compat.js');
      await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-database-compat.js');
    }

    // Wait for firebase to be fully ready (handles race condition with other scripts)
    await waitForFirebase();

    // Initialize Firebase app if not already initialized
    if (!firebase.apps.length) {
      firebase.initializeApp(FIREBASE_CONFIG);
    }

    db = firebase.database();
    return db;
  }

  /**
   * Wait for Firebase SDK to be fully loaded and ready
   */
  function waitForFirebase(timeout = 5000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const check = () => {
        if (typeof firebase !== 'undefined' && typeof firebase.database === 'function') {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('Firebase SDK load timeout'));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  /**
   * Load external script dynamically
   */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Fetch data from Firebase with caching
   */
  async function fetchData(path) {
    const cacheKey = path.replace('/', '');
    if (dataCache[cacheKey]) {
      return dataCache[cacheKey];
    }

    try {
      await initFirebase();
      const snapshot = await db.ref(path).once('value');
      const data = snapshot.val();
      dataCache[cacheKey] = data;
      return data;
    } catch (error) {
      console.error(`Error fetching ${path}:`, error);
      throw error;
    }
  }

  /**
   * Get years range from Firebase
   */
  async function getYears() {
    const yearsData = await fetchData('/years');
    if (!yearsData || !yearsData.range) {
      // Default fallback range
      return generateYearRange(2000, new Date().getFullYear() + 1);
    }
    return generateYearRange(yearsData.range.min, yearsData.range.max);
  }

  /**
   * Generate array of years
   */
  function generateYearRange(min, max) {
    const years = [];
    for (let year = max; year >= min; year--) {
      years.push(year.toString());
    }
    return years;
  }

  /**
   * Get makes from Firebase
   */
  async function getMakes() {
    const makesData = await fetchData('/makes');
    if (!makesData) return [];
    return Object.keys(makesData).sort();
  }

  /**
   * Get models for a specific make
   */
  async function getModels(make) {
    const makesData = await fetchData('/makes');
    if (!makesData || !makesData[make]) return [];
    return makesData[make].models || [];
  }

  /**
   * Check if a vehicle is compatible with a product
   */
  async function checkCompatibility(year, make, model, productHandle) {
    try {
      const vehicles = await fetchData('/vehicles');
      const racks = await fetchData('/racks');

      if (!vehicles || !racks) {
        return { compatible: null, message: 'Unable to verify compatibility' };
      }

      // Find matching vehicle
      const matchingVehicle = Object.entries(vehicles).find(([id, vehicle]) => {
        return vehicle.year === year &&
               vehicle.make === make &&
               vehicle.model === model;
      });

      if (!matchingVehicle) {
        return { compatible: null, message: 'Vehicle not found in database' };
      }

      const [vehicleId, vehicleData] = matchingVehicle;

      // Find rack by product handle
      const matchingRack = Object.entries(racks).find(([id, rack]) => {
        return rack.shopifyHandle === productHandle ||
               rack.shopifyProductId?.includes(productHandle);
      });

      if (!matchingRack) {
        return { compatible: null, message: 'Product compatibility data not available' };
      }

      const [rackId] = matchingRack;

      // Check if vehicle has this rack in compatible list
      const isCompatible = vehicleData.compatibleRacks?.includes(rackId);

      return {
        compatible: isCompatible,
        message: isCompatible
          ? 'This rack fits your vehicle!'
          : 'This rack may not fit your selected vehicle.'
      };
    } catch (error) {
      console.error('Compatibility check error:', error);
      return { compatible: null, message: 'Error checking compatibility' };
    }
  }

  /**
   * Get saved vehicles from localStorage
   */
  function getSavedVehicles() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Save vehicle to localStorage
   */
  function saveVehicle(year, make, model, name = '') {
    const vehicles = getSavedVehicles();
    const vehicleKey = `${year}-${make}-${model}`;

    // Check if already exists
    if (vehicles.some(v => `${v.year}-${v.make}-${v.model}` === vehicleKey)) {
      return false;
    }

    const vehicle = { year, make, model, savedAt: Date.now() };
    if (name && name.trim()) {
      vehicle.name = name.trim();
    }
    vehicles.push(vehicle);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicles));
    return true;
  }

  /**
   * Remove saved vehicle
   */
  function removeSavedVehicle(year, make, model) {
    let vehicles = getSavedVehicles();
    vehicles = vehicles.filter(v => !(v.year === year && v.make === make && v.model === model));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicles));
  }

  /**
   * Get saved customer email from localStorage
   */
  function getSavedEmail() {
    try {
      return localStorage.getItem(EMAIL_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Save customer email to localStorage
   */
  function saveEmail(email) {
    try {
      if (email && email.trim()) {
        localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
      }
    } catch (e) {}
  }

  /**
   * Capture customer email and vehicle data via Leitner Vehicle App proxy
   * This creates/updates a Shopify customer with vehicle metafields and handles newsletter opt-in
   */
  async function captureCustomerVehicle(email, vehicleInfo = {}, subscribeNewsletter = false) {
    if (!email || !email.trim()) return { success: false, message: 'No email provided' };

    try {
      const response = await fetch('/apps/leitner-vehicle/api/capture-vehicle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          year: vehicleInfo.year || '',
          make: vehicleInfo.make || '',
          model: vehicleInfo.model || '',
          nickname: vehicleInfo.name || vehicleInfo.nickname || '',
          subscribeNewsletter: !!subscribeNewsletter
        })
      });

      const result = await response.json();

      if (result.success) {
        // Save email on successful capture so it can be used for subsequent vehicle additions
        saveEmail(email);
        console.log('Customer vehicle captured:', email, vehicleInfo);
      } else {
        console.error('Failed to capture customer vehicle:', result.message);
      }

      return result;
    } catch (error) {
      console.error('Customer capture error:', error);
      return { success: false, message: 'Error saving vehicle' };
    }
  }

  /**
   * @deprecated Use captureCustomerVehicle instead
   * Kept for backward compatibility - now just calls captureCustomerVehicle
   */
  async function subscribeToNewsletter(email, vehicleInfo = {}) {
    return captureCustomerVehicle(email, vehicleInfo, true);
  }

  /**
   * Initialize a vehicle picker component
   */
  async function initVehiclePicker(container) {
    const productId = container.dataset.productId;
    const productHandle = container.dataset.productHandle;
    const redirectUrl = container.dataset.redirectUrl;
    const hideIfHasVehicle = container.dataset.hideIfHasVehicle === 'true';

    // Hide entire section if user has saved vehicles and hideIfHasVehicle is set
    if (hideIfHasVehicle) {
      const savedVehicles = getSavedVehicles();
      if (savedVehicles.length > 0) {
        const section = container.closest('section');
        if (section) {
          section.style.display = 'none';
        }
        return; // Don't initialize picker
      }
    }

    const yearSelect = container.querySelector('[data-vehicle-year]');
    const makeSelect = container.querySelector('[data-vehicle-make]');
    const modelSelect = container.querySelector('[data-vehicle-model]');
    const nameInput = container.querySelector('[data-vehicle-name]');
    const emailInput = container.querySelector('[data-vehicle-email]');
    const newsletterCheckbox = container.querySelector('[data-vehicle-newsletter]');
    const nameField = container.querySelector('[data-name-field]');
    const emailField = container.querySelector('[data-email-field]');
    const statusEl = container.querySelector('[data-vehicle-status]');
    const compatibleEl = container.querySelector('[data-status-compatible]');
    const incompatibleEl = container.querySelector('[data-status-incompatible]');
    const savedContainer = container.querySelector('[data-saved-vehicles]');
    const savedList = container.querySelector('[data-saved-list]');
    const saveBtn = container.querySelector('[data-save-vehicle]');
    const loadingEl = container.querySelector('[data-loading]');
    const errorEl = container.querySelector('[data-error]');
    const retryBtn = container.querySelector('[data-retry]');

    // Show loading
    function showLoading(show) {
      if (loadingEl) loadingEl.hidden = !show;
    }

    // Show error
    function showError(show) {
      if (errorEl) errorEl.hidden = !show;
    }

    // Update status display
    function updateStatus(result) {
      if (!statusEl) return;

      statusEl.hidden = result === null;
      if (compatibleEl) compatibleEl.hidden = result?.compatible !== true;
      if (incompatibleEl) incompatibleEl.hidden = result?.compatible !== false;

      // Update message if provided
      if (result?.message) {
        const messageEl = result.compatible
          ? compatibleEl?.querySelector('[data-status-message]')
          : incompatibleEl?.querySelector('[data-status-message]');
        if (messageEl) messageEl.textContent = result.message;
      }
    }

    // Populate select with options
    function populateSelect(select, options, placeholder) {
      select.innerHTML = `<option value="">${placeholder}</option>`;
      options.forEach(opt => {
        const option = document.createElement('option');
        option.value = opt;
        option.textContent = opt;
        select.appendChild(option);
      });
      select.disabled = options.length === 0;
    }

    // Render saved vehicles
    function renderSavedVehicles() {
      const vehicles = getSavedVehicles();
      if (!savedContainer || !savedList) return;

      savedContainer.hidden = vehicles.length === 0;
      savedList.innerHTML = '';

      vehicles.forEach(v => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vehicle-picker__saved-item';
        btn.innerHTML = `
          <span>${v.year} ${v.make} ${v.model}</span>
          <span class="vehicle-picker__saved-remove" data-remove>&times;</span>
        `;

        // Click to select
        btn.addEventListener('click', (e) => {
          if (e.target.closest('[data-remove]')) {
            removeSavedVehicle(v.year, v.make, v.model);
            renderSavedVehicles();
            return;
          }
          selectVehicle(v.year, v.make, v.model);
        });

        savedList.appendChild(btn);
      });
    }

    // Select a vehicle programmatically
    async function selectVehicle(year, make, model) {
      yearSelect.value = year;
      await handleYearChange();

      makeSelect.value = make;
      await handleMakeChange();

      modelSelect.value = model;
      await handleModelChange();
    }

    // Handle year change
    async function handleYearChange() {
      makeSelect.value = '';
      modelSelect.value = '';
      modelSelect.disabled = true;
      updateStatus(null);
      if (saveBtn) saveBtn.hidden = true;
      if (nameField) nameField.classList.remove('is-visible');
      if (nameInput) nameInput.value = '';
      if (emailInput) emailInput.value = '';

      if (!yearSelect.value) {
        makeSelect.disabled = true;
        return;
      }

      try {
        const makes = await getMakes();
        populateSelect(makeSelect, makes, 'Select Make');
      } catch (e) {
        console.error('Error loading makes:', e);
      }
    }

    // Handle make change
    async function handleMakeChange() {
      modelSelect.value = '';
      updateStatus(null);
      if (saveBtn) saveBtn.hidden = true;
      if (nameField) nameField.classList.remove('is-visible');
      if (nameInput) nameInput.value = '';
      if (emailInput) emailInput.value = '';

      if (!makeSelect.value) {
        modelSelect.disabled = true;
        return;
      }

      try {
        const models = await getModels(makeSelect.value);
        populateSelect(modelSelect, models, 'Select Model');
      } catch (e) {
        console.error('Error loading models:', e);
      }
    }

    // Handle model change
    async function handleModelChange() {
      if (!modelSelect.value) {
        updateStatus(null);
        if (saveBtn) saveBtn.hidden = true;
        if (nameField) {
          nameField.classList.remove('is-visible');
          nameField.hidden = true;
        }
        if (emailField) {
          emailField.classList.remove('is-visible');
          emailField.hidden = true;
        }
        return;
      }

      // Check compatibility
      const result = await checkCompatibility(
        yearSelect.value,
        makeSelect.value,
        modelSelect.value,
        productHandle
      );

      updateStatus(result);

      // Show save button and name field if all fields selected
      if (saveBtn) {
        saveBtn.hidden = false;
      }
      if (nameField) {
        nameField.classList.add('is-visible');
        nameField.hidden = false;
      }
      // Show email field and pre-populate with stored email (for modal pickers)
      if (emailField) {
        emailField.classList.add('is-visible');
        emailField.hidden = false;
        // Pre-fill with stored email if available and field is empty
        if (emailInput && !emailInput.value) {
          const storedEmail = getSavedEmail();
          if (storedEmail) {
            emailInput.value = storedEmail;
          }
        }
      }
    }

    // Save vehicle button click
    if (saveBtn) {
      saveBtn.addEventListener('click', async () => {
        if (yearSelect.value && makeSelect.value && modelSelect.value) {
          const vehicleName = nameInput ? nameInput.value : '';
          // Use entered email or fall back to saved email from previous capture
          const vehicleEmail = (emailInput ? emailInput.value : '') || getSavedEmail();
          const subscribeNewsletter = newsletterCheckbox ? newsletterCheckbox.checked : false;

          // Capture customer vehicle info and newsletter subscription via app proxy
          // This will sync to Shopify metafield using either the entered email or stored email
          if (vehicleEmail) {
            console.log('Syncing vehicle to Shopify for:', vehicleEmail);
            await captureCustomerVehicle(vehicleEmail, {
              year: yearSelect.value,
              make: makeSelect.value,
              model: modelSelect.value,
              name: vehicleName
            }, subscribeNewsletter);
          } else {
            console.log('No email provided - vehicle saved locally only, not synced to Shopify');
          }

          const saved = saveVehicle(yearSelect.value, makeSelect.value, modelSelect.value, vehicleName);
          if (saved) {
            renderSavedVehicles();

            // Dispatch event for status bar and other listeners
            document.dispatchEvent(new CustomEvent('vehicle:saved', {
              detail: {
                year: yearSelect.value,
                make: makeSelect.value,
                model: modelSelect.value,
                name: vehicleName,
                email: vehicleEmail,
                subscribedToNewsletter: subscribeNewsletter && !!vehicleEmail
              }
            }));

            // Clear inputs
            if (nameInput) nameInput.value = '';
            if (emailInput) emailInput.value = '';

            // Redirect if URL is configured
            if (redirectUrl) {
              saveBtn.textContent = 'Redirecting...';
              setTimeout(() => {
                // Build URL with vehicle params
                const url = new URL(redirectUrl, window.location.origin);
                url.searchParams.set('year', yearSelect.value);
                url.searchParams.set('make', makeSelect.value);
                url.searchParams.set('model', modelSelect.value);
                window.location.href = url.toString();
              }, 300);
              return;
            }

            saveBtn.textContent = 'Saved!';
            setTimeout(() => {
              saveBtn.textContent = 'Save This Vehicle';
            }, 2000);
          }
        }
      });
    }

    // Retry button click
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        showError(false);
        init();
      });
    }

    // Add event listeners
    yearSelect.addEventListener('change', handleYearChange);
    makeSelect.addEventListener('change', handleMakeChange);
    modelSelect.addEventListener('change', handleModelChange);

    // Initialize
    async function init() {
      showLoading(true);
      showError(false);

      try {
        const years = await getYears();
        populateSelect(yearSelect, years, 'Select Year');
        renderSavedVehicles();
        showLoading(false);
      } catch (e) {
        console.error('Error initializing vehicle picker:', e);
        showLoading(false);
        showError(true);
      }
    }

    await init();
  }

  /**
   * Initialize all vehicle pickers on the page
   */
  function initAllPickers() {
    const pickers = document.querySelectorAll('[data-vehicle-picker]');
    pickers.forEach(picker => {
      if (!picker.dataset.initialized) {
        picker.dataset.initialized = 'true';
        initVehiclePicker(picker);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPickers);
  } else {
    initAllPickers();
  }

  // Re-initialize on Shopify section render (for theme editor)
  document.addEventListener('shopify:section:load', initAllPickers);

  // Expose for external use
  window.LeitnerVehicleSelector = {
    init: initAllPickers,
    initPicker: initVehiclePicker,
    checkCompatibility,
    getSavedVehicles,
    saveVehicle,
    removeSavedVehicle,
    getSavedEmail,
    saveEmail,
    captureCustomerVehicle,
    subscribeToNewsletter // deprecated, use captureCustomerVehicle
  };
})();
